(function (browser, factory) {
  'use strict';
  var api = factory();
  if (browser) browser.TeaRoutine = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  var GOALS = ['stress', 'sleep', 'digestion', 'snacking', 'movement', 'heart', 'explore'];
  var MOMENTS = ['morning', 'midday', 'afternoon', 'evening'];
  // 22 Oct 2026: a shopper may pick more teas than cups (2 cups, 4 teas). Each tea past the cup count gets an
  // extra slot. It holds a different tea that fits one of the chosen times, switched in from day to day.
  var EXTRAS = ['extra-1', 'extra-2', 'extra-3'];
  function isExtra(slot) { return EXTRAS.indexOf(slot) !== -1; }
  function unique(a) { return Array.from(new Set(Array.isArray(a) ? a : [])); }
  function cents(n) { return Number.isSafeInteger(n) && n >= 0; }
  function normalize(answers) {
    var a = answers || {};
    var cups = [2, 3, 4].indexOf(a.dailyCups) !== -1 ? a.dailyCups : 2;
    var moments = unique(a.moments).filter(function (m) { return MOMENTS.indexOf(m) !== -1; }).slice(0, cups);
    var count = Number.isSafeInteger(a.count) ? Math.min(4, Math.max(1, a.count)) : cups;
    // Extras only once every cup has its time: one per tea past the cup count.
    var extras = moments.length === cups ? EXTRAS.slice(0, Math.max(0, count - cups)) : [];
    return {
      goals: unique(a.goals).filter(function (g) { return GOALS.indexOf(g) !== -1; }),
      moments: moments,
      count: count,
      extras: extras,
      slots: moments.concat(extras),
      // No price ceiling is inferred from the price of another tea.
      budgetCents: a.budgetCents === undefined ? Number.MAX_SAFE_INTEGER : a.budgetCents,
      validBudget: a.budgetCents === undefined || cents(a.budgetCents)
    };
  }
  function compareProducts(a, b) {
    var ar = Number.isFinite(a.rank) ? a.rank : Number.MAX_SAFE_INTEGER;
    var br = Number.isFinite(b.rank) ? b.rank : Number.MAX_SAFE_INTEGER;
    return ar - br || a.id.localeCompare(b.id);
  }
  function catalog(products) {
    var seen = new Set();
    return (Array.isArray(products) ? products : []).filter(function (p) {
      if (!p || typeof p.id !== 'string' || !p.id || !cents(p.priceCents) || seen.has(p.id)) return false;
      seen.add(p.id); return true;
    }).slice().sort(compareProducts);
  }
  function value(assignments, moment) {
    return assignments && Object.prototype.hasOwnProperty.call(assignments, moment) ? assignments[moment] : null;
  }
  // A cup slot takes a tea made for that time. An extra slot takes a tea made for any of the chosen times.
  function fits(p, slot, a) {
    if (p.available !== true || !Array.isArray(p.moments)) return false;
    if (isExtra(slot)) return a.moments.some(function (m) { return p.moments.indexOf(m) !== -1; });
    return p.moments.indexOf(slot) !== -1;
  }
  function goalScore(p, a) {
    return a.goals.filter(function (g) { return g !== 'explore' && Array.isArray(p.goals) && p.goals.indexOf(g) !== -1; }).length;
  }
  function selectedProducts(products, assignments) {
    var ids = new Set(MOMENTS.concat(EXTRAS).map(function (m) { return value(assignments, m); }));
    return catalog(products).filter(function (p) { return ids.has(p.id); });
  }
  // One entry per filled slot, in slot order, so two Cortisol AM cups are two pouches.
  function selectedPouches(products, assignments, answers) {
    var a = answers && Array.isArray(answers.slots) ? answers : normalize(answers);
    var map = new Map(catalog(products).map(function (p) { return [p.id, p]; }));
    return (a.slots || []).map(function (m) { return map.get(value(assignments, m)); }).filter(Boolean);
  }
  function withinLimits(all, a, assignments) {
    if (!a.validBudget) return false;
    var pouches = selectedPouches(all, assignments, a);
    if (pouches.length > a.count) return false;
    var sum = pouches.reduce(function (total, p) { return total + p.priceCents; }, 0);
    return Number.isSafeInteger(sum) && sum <= a.budgetCents;
  }
  // Auto-suggest still prefers a different extra tea. The shopper may pick the same tea twice.
  function extrasDistinct(a, assignments) {
    return a.extras.every(function (e) {
      var id = value(assignments, e);
      return id === null || id === undefined || !Object.keys(assignments).some(function (k) { return k !== e && value(assignments, k) === id; });
    });
  }
  function validAssignments(all, a, assignments) {
    var map = new Map(all.map(function (p) { return [p.id, p]; }));
    var keys = Object.keys(assignments || {});
    for (var i = 0; i < keys.length; i += 1) {
      var m = keys[i], id = value(assignments, m);
      if (id === null || id === undefined) continue;
      if (a.slots.indexOf(m) === -1 || !map.has(id) || !fits(map.get(id), m, a)) return false;
    }
    return withinLimits(all, a, assignments);
  }
  function place(all, a, assignments, slot, id) {
    var next = Object.assign({}, assignments || {});
    next[slot] = id;
    return next;
  }
  function assign(products, answers, assignments, slot, id) {
    var a = normalize(answers);
    if (a.slots.indexOf(slot) === -1) return null;
    var all = catalog(products);
    if (id === null) return place(all, a, assignments, slot, null);
    var p = all.find(function (product) { return product.id === id; });
    if (!p || !fits(p, slot, a)) return null;
    var next = place(all, a, assignments, slot, id);
    return validAssignments(all, a, next) ? next : null;
  }
  function canAssign(products, answers, assignments, slot, id) {
    return assign(products, answers, assignments, slot, id) !== null;
  }
  function slotsFor(answers) { return normalize(answers).slots.slice(); }
  function optionsForSlot(products, answers, assignments, slot) {
    var a = normalize(answers);
    return catalog(products).filter(function (p) { return canAssign(products, answers, assignments, slot, p.id); })
      .sort(function (x, y) { return goalScore(y, a) - goalScore(x, a) || compareProducts(x, y); });
  }
  function suggestAssignments(products, answers) {
    var a = normalize(answers), all = catalog(products), empty = {};
    a.slots.forEach(function (m) { empty[m] = null; });
    if (!a.validBudget || !a.moments.length) return empty;
    var best = empty, bestScore = null, working = Object.assign({}, empty);
    function score(assignments) {
      var selected = selectedProducts(all, assignments);
      var covered = a.goals.filter(function (g) { return g !== 'explore' && selected.some(function (p) { return (p.goals || []).indexOf(g) !== -1; }); }).length;
      var filled = a.slots.filter(function (m) { return value(assignments, m) !== null; }).length;
      var matchesIn = function (list) { return list.reduce(function (n, m) { var p = all.find(function (p) { return p.id === value(assignments, m); }); return n + (p ? goalScore(p, a) : 0); }, 0); };
      // 5 Oct 2026: the shopper now picks how many different teas to start with (2, 3 or 4), so a
      // suggestion fills that number when the chosen times allow it, instead of the fewest teas.
      // 22 Oct 2026: goal-matching teas go in the cups before the extras.
      return [filled, covered, selected.length, matchesIn(a.moments), matchesIn(a.slots)];
    }
    function better(s) {
      if (!bestScore) return true;
      for (var i = 0; i < s.length; i += 1) { if (s[i] !== bestScore[i]) return s[i] > bestScore[i]; }
      return false;
    }
    function visit(index) {
      if (index === a.slots.length) {
        var s = score(working);
        if (better(s)) { best = Object.assign({}, working); bestScore = s; }
        return;
      }
      var slot = a.slots[index];
      all.filter(function (p) { return fits(p, slot, a); }).sort(function (x, y) { return goalScore(y, a) - goalScore(x, a) || compareProducts(x, y); }).forEach(function (p) {
        working[slot] = p.id;
        if (extrasDistinct(a, working) && withinLimits(all, a, working)) visit(index + 1);
      });
      working[slot] = null; visit(index + 1); working[slot] = null;
    }
    visit(0); return best;
  }
  return Object.freeze({ optionsForSlot: optionsForSlot, suggestAssignments: suggestAssignments, selectedProducts: selectedProducts, selectedPouches: selectedPouches, canAssign: canAssign, assign: assign, slotsFor: slotsFor, isExtra: isExtra, EXTRAS: EXTRAS.slice() });
});
